import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate } from '@/shared/utils/format';

interface ProjectTimeIndicatorProps {
  startDate: string | null;
  endDate: string | null;
}

type ProjectStatus = 'early_stage' | 'in_progress' | 'overdue';

function calculateTimeRemaining(
  startDate: string | null,
  endDate: string | null,
  t: (key: string, options?: any) => string,
): {
  status: ProjectStatus | null;
  daysRemaining: number;
  progress: number;
  deadlineText: string;
  timeText: string;
} {
  if (!startDate || !endDate) {
    return {
      status: null,
      daysRemaining: 0,
      progress: 0,
      deadlineText: '',
      timeText: '',
    };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  const now = new Date();

  const totalDays = Math.ceil(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  const daysElapsed = Math.ceil(
    (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  const daysRemaining = Math.ceil(
    (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  const progress = Math.max(0, Math.min(100, (daysElapsed / totalDays) * 100));

  let status: ProjectStatus;
  let timeText: string;
  let deadlineText: string;

  const formattedDeadlineDate = formatDate({
    date: endDate,
    formatType: 'digits',
  });

  if (daysRemaining < 0) {
    status = 'overdue';
    const daysOverdue = Math.abs(daysRemaining);
    if (daysOverdue === 1) {
      timeText = `1 ${t('timeUnits.day')}`;
    } else {
      timeText = `${daysOverdue} ${t('timeUnits.days')}`;
    }
    deadlineText = t('deadline.over', { date: formattedDeadlineDate });
  } else {
    const weeks = Math.floor(daysRemaining / 7);

    if (progress < 25) {
      status = 'early_stage';
    } else if (progress < 75) {
      status = 'in_progress';
    } else {
      status = 'in_progress';
    }

    if (weeks > 0) {
      if (weeks === 1) {
        timeText = `1 ${t('timeUnits.week')}`;
      } else {
        timeText = `${weeks} ${t('timeUnits.weeks')}`;
      }
    } else if (daysRemaining === 1) {
      timeText = `1 ${t('timeUnits.day')}`;
    } else {
      timeText = `${daysRemaining} ${t('timeUnits.days')}`;
    }

    deadlineText = t('deadline.until', { date: formattedDeadlineDate });
  }

  return {
    status,
    daysRemaining,
    progress,
    timeText,
    deadlineText,
  };
}

export const ProjectTimeIndicator = ({
  startDate,
  endDate,
}: ProjectTimeIndicatorProps) => {
  const { t } = useTranslation('overview', {
    keyPrefix: 'OverviewPage.kpiCards',
  });

  const { status, progress, timeText, deadlineText } = useMemo(
    () => calculateTimeRemaining(startDate, endDate, t),
    [startDate, endDate, t],
  );

  if (!startDate || !endDate || !status) {
    return null;
  }

  const statusLabels: Record<ProjectStatus, string> = {
    early_stage: t('projectStatus.earlyStage'),
    in_progress: t('projectStatus.inProgress'),
    overdue: t('projectStatus.overdue'),
  };

  const progressColors: Record<ProjectStatus, string> = {
    early_stage: '#897ABA',
    in_progress: '#897ABA',
    overdue: 'url(#pinkRedGradient)',
  };

  const size = 100;
  const strokeWidth = 16;
  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * radius;
  const strokeDasharray = circumference;
  const cappedProgress = Math.max(0, Math.min(100, progress));
  const strokeDashoffset =
    circumference - (cappedProgress / 100) * circumference;

  return (
    <div className="flex flex-1 flex-col items-center justify-between rounded-md bg-primary-shade-grey-500/5 px-6 py-3.5">
      <div
        className={`rounded-full bg-white px-2 py-0.5 text-2xs font-semibold text-black`}
      >
        {statusLabels[status]}
      </div>

      <div className="relative flex h-[65px] w-full items-center justify-center">
        <svg
          width={size}
          height={size / 2 + strokeWidth / 2}
          viewBox={`0 0 ${size} ${size / 2 + strokeWidth / 2}`}
          className="overflow-visible"
        >
          <defs>
            <linearGradient
              id="pinkRedGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="hsla(305, 91%, 45%, 0.7)" />
              <stop offset="100%" stopColor="hsla(359, 100%, 30%, 0.9)" />
            </linearGradient>
          </defs>
          <path
            d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
            fill="none"
            stroke="rgba(255, 255, 255, 0.15)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <path
            d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
            fill="none"
            stroke={progressColors[status]}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-300 ease-in-out"
          />
          {status === 'overdue' && (
            <text
              x={size / 2}
              y={size / 4 + radius / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="url(#pinkRedGradient)"
              fontSize={23}
              fontWeight="900"
            >
              !
            </text>
          )}
        </svg>
      </div>

      <div className="flex flex-col items-center gap-1">
        <p className="font-semibold text-white">{timeText}</p>
        <p className="text-center text-3xs text-primary-shade-grey-500">
          {deadlineText}
        </p>
      </div>
    </div>
  );
};
